/**
 * @file: useWeixinLoginBinding.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useCurrentUserDangerously } from '@euler/app/flows/auth/context';
import { useAppLoading } from '@euler/components/loading/Provider';
import {
  useWeixinLogin,
  WeixinLoginErrorResult,
} from '@euler/functions/useWeixinLogin';
import { AuthenticatorResult, WeixinAuthProvider } from '@euler/model/auth';
import { useServiceFactory } from '@euler/services/factory';
import { showAlert } from '@euler/utils';
import { usePersistFn, useRefreshableAsyncResource } from '@euler/utils/hooks';
import { Platform } from 'react-native';
import Toast from 'react-native-root-toast';

export const useWeixinLoginBinding = () => {
  const loading = useAppLoading();
  const { user } = useCurrentUserDangerously();
  const { userService } = useServiceFactory();

  const fetch = usePersistFn(() => {
    return userService.getWeixinLoginBindStatus();
  });

  const {
    value: bindStatus,
    error: bindStatusError,
    update: setBindStatus,
  } = useRefreshableAsyncResource(fetch, 'user:weixin-binding-status', null);

  const loginWeixin = useWeixinLogin({
    uid: user.uid,
    onSuccess: async (
      _nonce: string,
      result: AuthenticatorResult<WeixinAuthProvider>,
    ) => {
      loading.hide();

      if (result.nextAction?.type === 'federation-continuation') {
        // continue with phone
        throw new Error('Unexpected error');
      } else {
        setBindStatus(true);
      }
    },
    onError: (result: WeixinLoginErrorResult) => {
      loading.hide();

      if (result.type === 'weixin_not_installed') {
        Toast.show('微信未安装', {
          duration: 1000,
          position: Toast.positions.BOTTOM,
        });
      } else if (result.type === 'weixin_sdk_error') {
        Toast.show(result.message ?? '微信授权失败', {
          duration: 1500,
          position: Toast.positions.BOTTOM,
        });
      } else {
        void showAlert(result.error.message);
      }
    },
  });

  const bindAsync = usePersistFn(async () => {
    if (Platform.OS === 'web') return;
    loading.show();

    await loginWeixin();
  });

  const unbindAsync = usePersistFn(async () => {
    await userService.removeWeixinLoginBinding();
    setBindStatus(false);
  });

  return { bindStatus, bindStatusError, bindAsync, unbindAsync };
};
